/**
 * El informe tal y como se le manda al cliente.
 *
 * Lo que hay guardado es un informe congelado con todo dentro: lo que se le
 * enseña, lo interno, el plan, los problemas técnicos. Esto se queda con la
 * mitad que se envía y la escribe en cristiano.
 *
 * **Empieza por los contactos, no por las posiciones.** Un cliente paga por
 * clientes. «Has subido de la 15 a la 8» le suena bien y no le dice nada;
 * «te han escrito 29 personas este mes» sí. Las posiciones van después, como
 * explicación de por qué pasa lo primero.
 *
 * **Y no se inventa nada.** Si un dato no está —porque esa web no mide
 * conversiones, o porque aún no hay informe anterior— se omite la frase entera
 * en vez de rellenarla. Un informe con un hueco es honesto; uno con un número
 * de relleno se cae en la primera pregunta.
 */
/** «un 24 % más», «un 12 % menos», o nada si no hay con qué comparar. */
function variacion(ahora, antes) {
    if (!antes)
        return "";
    const cambio = Math.round(((ahora - antes) / antes) * 100);
    if (cambio === 0)
        return ", igual que el mes pasado";
    return `, un ${Math.abs(cambio)} % ${cambio > 0 ? "más" : "menos"} que el mes pasado`;
}
const MESES = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
];
function nombreDelMes(mes) {
    const [anio, num] = mes.split("-");
    const n = Number(num) - 1;
    return MESES[n] ? `${MESES[n]} de ${anio}` : mes;
}
/**
 * El informe en markdown, listo para pegar en un correo.
 *
 * Devuelve texto y no un objeto a propósito: el que lo manda tiene que poder
 * leerlo entero antes de enviarlo, y un objeto invita a que alguien lo pinte
 * sin mirarlo.
 */
/**
 * Una línea de «qué hemos hecho», dicha para el cliente (22/10/2026).
 *
 * Las líneas llegan como las escribe el sistema —«Cambiar el SEO de 58
 * página(s) de gesclean.es — 65 cambio(s) escritos y releídos en gesclean.es»,
 * o un punto de Trello con el nombre de la ficha detrás entre paréntesis— y
 * así llegaban al documento que lee el cliente. Esto no cambia lo que dicen:
 * quita el «(s)» de plantilla, el dominio repetido y el nombre de la ficha.
 *
 * Se hace al PINTAR, no al guardar: el informe congelado se queda como se
 * generó, y si esto se equivoca se corrige aquí y se arregla en todos.
 */
export function dichoParaElCliente(linea, cliente) {
    /*
     * Sólo la primera línea. Un punto de Trello trae debajo sus notas de trabajo
     * —«Cómo:», «Dónde:» y una lista de direcciones— y eso es para quien lo hace,
     * no para el cliente.
     */
    let t = linea.trim().split(/\r?\n/)[0].trim();
    /* «1 páginas» → «1 página»: el plural lo pone a ciegas quien junta el número. */
    t = t.replace(/(^|[^\d.,])1 (páginas|enlaces|cambios|imágenes|errores|URLs)\b/g, (_, antes, p) => `${antes}1 ${{ páginas: "página", enlaces: "enlace", cambios: "cambio", imágenes: "imagen", errores: "error", URLs: "URL" }[p]}`);
    /* «58 página(s)» → «58 páginas»; «1 página(s)» → «1 página». */
    t = t.replace(/(\d[\d.]*)\s+([\p{L}]+)\(s\)/gu, (_, n, palabra) => Number(n.replace(/\./g, "")) === 1 ? `${n} ${palabra}` : `${n} ${palabra}s`);
    /* Lo que queda suelto con «(s)», sin número delante. */
    t = t.replace(/([\p{L}])\(s\)/gu, "$1s");
    /* «— 65 cambios escritos y releídos en gesclean.es»: el dominio ya está dicho. */
    t = t.replace(/\s+en\s+[a-z0-9.-]+\.[a-z]{2,}\s*$/i, (m) => {
        const dominio = m.trim().replace(/^en\s+/i, "");
        return t.slice(0, t.length - m.length).includes(dominio) ? "" : m;
    });
    t = t.replace(/escritos y releídos/g, "escritos y comprobados");
    /* El nombre de la ficha de Trello al final, entre paréntesis. */
    if (cliente) {
        const nombre = cliente.trim().toLowerCase();
        /* El paréntesis que abre el que cierra al final: la ficha lleva otros dentro. */
        let i = -1;
        if (t.endsWith(")")) {
            let hondo = 0;
            for (let j = t.length - 1; j >= 0; j--) {
                if (t[j] === ")")
                    hondo++;
                else if (t[j] === "(" && --hondo === 0) {
                    i = j;
                    break;
                }
            }
        }
        if (i > 0 && t.slice(i).toLowerCase().includes(nombre))
            t = t.slice(0, i);
    }
    return t.trim();
}
export function informeParaElCliente(cuerpo) {
    const c = cuerpo.paraElCliente ?? {};
    const partes = [];
    partes.push(`# Cómo ha ido ${nombreDelMes(cuerpo.mes ?? "")}`);
    partes.push("");
    if (cuerpo.lectura?.trim()) {
        partes.push(`_${cuerpo.lectura.trim()}_`);
        partes.push("");
    }
    // ── Lo que paga: los contactos ────────────────────────────────────────────
    if (c.contactos && c.contactos.total > 0) {
        const desglose = [
            c.contactos.formulario ? `${c.contactos.formulario} por formulario` : "",
            c.contactos.telefono ? `${c.contactos.telefono} llamadas` : "",
            c.contactos.whatsapp ? `${c.contactos.whatsapp} por WhatsApp` : "",
        ].filter(Boolean);
        partes.push(`**Te han contactado ${c.contactos.total} personas** desde Google${variacion(c.contactos.total, c.contactos.antes)}.`);
        if (desglose.length)
            partes.push(`Han llegado ${desglose.join(", ")}.`);
        partes.push("");
    }
    // ── Y la explicación de por qué ───────────────────────────────────────────
    if (c.busqueda) {
        partes.push(`Has aparecido en Google **${c.busqueda.impresiones.toLocaleString("es-ES")} veces** y te han pinchado **${c.busqueda.clics}**${variacion(c.busqueda.clics, c.busqueda.clicsAntes)}.`);
        partes.push("");
    }
    if (c.trafico) {
        partes.push(`A la web han entrado **${c.trafico.sesiones.toLocaleString("es-ES")} visitas**${variacion(c.trafico.sesiones, c.trafico.sesionesAntes)}.`);
        partes.push("");
    }
    if (c.servicio && c.servicio.cuantas > 0) {
        partes.push("## Dónde estás en Google");
        partes.push("");
        // La concordancia importa en un texto que sale con el nombre de la agencia:
        // «1 están en la primera página» lo lee el cliente antes que el dato.
        const enPrimera = c.servicio.enPrimeraPagina;
        partes.push(`De las ${c.servicio.cuantas} búsquedas que trabajamos, **${enPrimera} ${enPrimera === 1 ? "está" : "están"} en la primera página**.`);
        if (c.servicio.mejoran > 0 || c.servicio.empeoran > 0) {
            partes.push(`Este mes ${c.servicio.mejoran} han subido y ${c.servicio.empeoran} han bajado.`);
        }
        partes.push("");
    }
    // ── Cuánta gente busca lo que trabajamos (Google Ads, 25/10/2026) ──────────
    if (c.demanda && c.demanda.total > 0 && c.demanda.keywords.length > 0) {
        const d = c.demanda;
        const n = (x) => x.toLocaleString("es-ES");
        partes.push(`## Cuánta gente busca lo que trabajamos en ${d.zona}`);
        partes.push("");
        partes.push(`Las búsquedas que trabajamos suman **${n(d.total)} búsquedas al mes en ${d.zona}**, según Google. Las que más se buscan:`);
        partes.push("");
        for (const k of d.keywords.filter((x) => x.busquedas > 0).slice(0, 10)) {
            const donde = k.posicion === null ? "todavía no apareces" : k.posicion <= 10 ? `estás en la ${k.posicion}, primera página` : `estás en la ${k.posicion}`;
            partes.push(`- **${k.keyword}**: ${n(k.busquedas)} al mes · ${donde}`);
        }
        partes.push("");
    }
    // ── Las IAs, que es lo que nadie más le está contando ─────────────────────
    if (Array.isArray(c.enLasIas) && c.enLasIas.length > 0) {
        const salen = c.enLasIas.filter((v) => v.teNombran);
        partes.push("## Cuando preguntan a una inteligencia artificial");
        partes.push("");
        partes.push(salen.length > 0
            ? /*
               * «Sales en 1 de las 1 búsquedas» era lo que salía, y esto lo lee un
               * cliente. Con una sola medida no hay proporción; y cuando sales en
               * TODAS, decir «2 de las 2» es contar una proporción para esconder
               * una buena noticia. Tres frases porque son tres situaciones.
               */
                `Cuando alguien le pide a una IA que le recomiende, **${c.enLasIas.length === 1
                    ? "sales en la búsqueda que medimos"
                    : salen.length === c.enLasIas.length
                        ? `sales en las ${c.enLasIas.length} búsquedas que medimos`
                        : `sales en ${salen.length} de las ${c.enLasIas.length} búsquedas que medimos`}**${salen[0]?.puesto ? `, en «${salen[0].keyword}» el ${salen[0].puesto}º de ${salen[0].deCuantos}` : ""}.`
            : `Todavía no sales recomendado cuando alguien se lo pregunta a una IA. Es un sitio nuevo donde estar y estamos trabajándolo.`);
        partes.push("");
        /*
         * Y en cuál (25/10/2026): desde que ChatGPT se mide cada semana hay varias
         * IAs, y «sales en 4 de 12» mezclaba tres. Sólo si hay más de una.
         */
        const ias = [...new Set(c.enLasIas.map((v) => v.ia).filter((x) => !!x))];
        if (ias.length > 1) {
            for (const ia of ias) {
                const suyas = c.enLasIas.filter((v) => v.ia === ia);
                const si = suyas.filter((v) => v.teNombran);
                const mejor = si.filter((v) => v.puesto).sort((a, b) => a.puesto - b.puesto)[0];
                partes.push(`- **${ia}**: ${si.length === 0 ? "todavía no te recomienda" : `te recomienda en ${si.length} de ${suyas.length}`}${mejor ? ` (en «${mejor.keyword}», el ${mejor.puesto}º)` : ""}`);
            }
            partes.push("");
        }
    }
    // ── El trabajo, que es lo que justifica la factura ────────────────────────
    /**
     * Lo arreglado se dice como arreglado.
     *
     * Los problemas llegan nombrados como problemas —«Velocidad de carga de la
     * página lenta: 8 páginas»— y puestos bajo «qué hemos hecho» se leen como
     * una queja en vez de como un trabajo. El cliente no distingue: lee la
     * frase.
     */
    /*
     * Los contenidos van ANTES del trabajo técnico, y en su propia sección.
     *
     * Es lo único del informe que el cliente puede ir a ver y leer. «Arreglado —
     * canónicas duplicadas» no significa nada para él; «hemos publicado
     * *Cortinas para salón: cómo elegirlas*» sí, y además le recuerda que hay
     * algo suyo nuevo en su web.
     */
    const publicados = c.contenidosPublicados ?? [];
    if (publicados.length > 0) {
        partes.push("## Lo que hemos publicado");
        partes.push("");
        for (const p of publicados)
            partes.push(`- ${p.titulo} _(${p.canal})_`);
        partes.push("");
    }
    const trabajo = [
        ...(c.seHizo ?? []),
        ...(c.problemasArreglados ?? []).map((p) => `Arreglado — ${p}`),
    ];
    if (trabajo.length > 0) {
        partes.push("## Qué hemos hecho");
        partes.push("");
        for (const t of trabajo)
            partes.push(`- ${dichoParaElCliente(t, cuerpo.cliente)}`);
        partes.push("");
    }
    /*
     * Y si lo de antes funcionó (8.7): lo medido 30 días después de aplicarlo.
     * Se dice como lo que es, que coincidió en el tiempo: en un mes le pasan a
     * una web más cosas que las nuestras.
     */
    const funciono = c.siFunciono ?? [];
    if (funciono.length > 0) {
        partes.push("## Cómo ha respondido Google a lo de meses anteriores");
        partes.push("");
        for (const f of funciono)
            partes.push(`- ${dichoParaElCliente(f, cuerpo.cliente)}`);
        partes.push("");
    }
    /*
     * La estrategia va justo antes del plan, y no al principio.
     *
     * Al principio le pide al cliente que se crea una intención antes de haberle
     * enseñado ni un resultado. Aquí ya ha visto lo que pasó, y entonces «hacia
     * dónde vamos» se lee como la consecuencia y no como una promesa.
     */
    if (cuerpo.estrategia?.trim()) {
        partes.push("## Hacia dónde vamos");
        partes.push("");
        partes.push(cuerpo.estrategia.trim());
        partes.push("");
    }
    if ((cuerpo.planDelMesQueViene?.length ?? 0) > 0) {
        partes.push("## Qué haremos el mes que viene");
        partes.push("");
        for (const a of cuerpo.planDelMesQueViene.slice(0, 5))
            partes.push(`- ${a.queHacer}`);
        partes.push("");
    }
    /**
     * Si la medición es compartida, se dice EN EL INFORME.
     *
     * Cinco clínicas del Grupo Scanner comparten web: mandarle a cada una un
     * informe con el tráfico de las cinco como suyo sería cobrar cinco veces por
     * el mismo resultado.
     */
    if (cuerpo.medicionCompartida) {
        partes.push("---");
        partes.push("");
        partes.push(`_${cuerpo.medicionCompartida}_`);
        partes.push("");
    }
    return partes.join("\n").trim();
}
//# sourceMappingURL=informe-cliente.js.map